import React, {
    FunctionComponent,
    PropsWithChildren,
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
} from 'react';
import { CalendarContext } from '../CalendarContext';

type CalendarSelectContextShape = {
    isDaySelected: (day: { from: Date; to: Date }) => boolean;
    onDaySelected: (day: { from: Date; to: Date }) => void;
    isDayDisabled: (day: { from: Date; to: Date }) => boolean;
};

export const CalendarSelectContext = createContext<CalendarSelectContextShape>({
    isDaySelected: () => false,
    onDaySelected: () => {},
    isDayDisabled: () => false,
});

export const CalendarSelectContextProvider: FunctionComponent<
    PropsWithChildren<{
        isDaySelected: (day: { from: Date; to: Date }) => boolean;
        onDaySelected: (day: { from: Date; to: Date }) => void;
        isDayDisabled: (day: { from: Date; to: Date }) => boolean;
    }>
> = ({ isDaySelected, onDaySelected: _onDaySelected, isDayDisabled, children }) => {
    const { setActiveDate } = useContext(CalendarContext);

    const onDaySelected = useCallback(
        (nextDay: { from: Date; to: Date }) => {
            setActiveDate(nextDay.from);
            _onDaySelected(nextDay);
        },
        [_onDaySelected, setActiveDate]
    );

    const context = useMemo((): CalendarSelectContextShape => {
        return {
            isDaySelected,
            onDaySelected,
            isDayDisabled,
        };
    }, [isDaySelected, onDaySelected, isDayDisabled]);

    return (
        <CalendarSelectContext.Provider value={context}>{children}</CalendarSelectContext.Provider>
    );
};
